import type { TripPreset, TripPoolItem } from "@/mocks/tripPlanner";
import { getTripPool } from "@/lib/publicGuide";

/** Quick id -> item lookup for resolving saved plan entries. */
export function buildPoolLookup(): Record<string, TripPoolItem> {
  const lookup: Record<string, TripPoolItem> = {};
  getTripPool().forEach((item) => {
    lookup[item.id] = item;
  });
  return lookup;
}

/**
 * Turns a ready-made preset into a builder plan.
 * Each preset stop is matched to a real pool item (so it can be tweaked in the
 * builder) via its link, without repeating the same item twice in one day.
 */
export function buildPlanFromPreset(preset: TripPreset): {
  dayCount: number;
  plan: Record<number, string[]>;
} {
  const byHref: Record<string, TripPoolItem[]> = {};
  getTripPool().forEach((item) => {
    if (!byHref[item.href]) byHref[item.href] = [];
    byHref[item.href].push(item);
  });

  const plan: Record<number, string[]> = {};
  preset.days.forEach((day, dayIndex) => {
    const used = new Set<string>();
    const ids: string[] = [];
    day.stops.forEach((stop) => {
      if (!stop.href) return;
      const candidates = byHref[stop.href] || [];
      const match = candidates.find((candidate) => !used.has(candidate.id));
      if (match) {
        used.add(match.id);
        ids.push(match.id);
      }
    });
    plan[dayIndex] = ids;
  });

  return { dayCount: preset.days.length, plan };
}

/**
 * Rough per-hour rates used for the running budget estimate.
 * Durations are the driver — the cost is simply hours x the stop's rate.
 */
export const HOURLY_RATES: Record<TripPoolItem["type"], number> = {
  experience: 450,
  farm: 250,
  culture: 300,
};

/** Pulls the leading number of hours out of a duration string like "8 hrs". */
export function parseDurationHours(duration: string): number {
  const match = duration.match(/\d+(\.\d+)?/);
  return match ? parseFloat(match[0]) : 0;
}

/** Rough cost for a single stop, derived from its duration and type rate. */
export function estimateItemCost(item: TripPoolItem): number {
  return Math.round(parseDurationHours(item.duration) * HOURLY_RATES[item.type]);
}

/** Formats a number as Indian rupees, e.g. 3600 -> ₹3,600. */
export function formatRupees(amount: number): string {
  return `₹${Math.round(amount).toLocaleString("en-IN")}`;
}

export interface ItinerarySummary {
  days: number;
  stops: number;
  hours: number;
  cost: number;
}

/** Rolls a plan up into totals for the itinerary panel, print page and enquiry form. */
export function summarizeItinerary(
  plan: Record<number, string[]>,
  dayCount: number,
): ItinerarySummary {
  const lookup = buildPoolLookup();
  let stops = 0;
  let hours = 0;
  let cost = 0;

  for (let d = 0; d < dayCount; d += 1) {
    (plan[d] || []).forEach((id) => {
      const item = lookup[id];
      if (!item) return;
      stops += 1;
      hours += parseDurationHours(item.duration);
      cost += estimateItemCost(item);
    });
  }

  return { days: dayCount, stops, hours: Math.round(hours * 10) / 10, cost };
}

/** Rolls a single day up into totals so each day can show its own estimate. */
export function summarizeDay(plan: Record<number, string[]>, dayIndex: number): ItinerarySummary {
  const lookup = buildPoolLookup();
  let stops = 0;
  let hours = 0;
  let cost = 0;

  (plan[dayIndex] || []).forEach((id) => {
    const item = lookup[id];
    if (!item) return;
    stops += 1;
    hours += parseDurationHours(item.duration);
    cost += estimateItemCost(item);
  });

  return { days: 1, stops, hours: Math.round(hours * 10) / 10, cost };
}

export interface StopLocation {
  query: string;
  label: string;
}

/** Distinct places covered by the current plan, in the order they were added. */
export function collectStopLocations(
  plan: Record<number, string[]>,
  dayCount: number,
): StopLocation[] {
  const lookup = buildPoolLookup();
  const seen = new Set<string>();
  const locations: StopLocation[] = [];

  for (let d = 0; d < dayCount; d += 1) {
    (plan[d] || []).forEach((id) => {
      const item = lookup[id];
      if (!item || !item.mapQuery || seen.has(item.mapQuery)) return;
      seen.add(item.mapQuery);
      locations.push({
        query: item.mapQuery,
        label: item.mapQuery.split(",")[0].trim(),
      });
    });
  }

  return locations;
}

/**
 * The place the print-page map should centre on — the area where most of the
 * chosen stops sit, falling back to the whole Konaseema delta.
 */
export function pickMapCenter(plan: Record<number, string[]>, dayCount: number): string {
  const lookup = buildPoolLookup();
  const counts: Record<string, number> = {};

  for (let d = 0; d < dayCount; d += 1) {
    (plan[d] || []).forEach((id) => {
      const item = lookup[id];
      if (!item || !item.mapQuery) return;
      counts[item.mapQuery] = (counts[item.mapQuery] || 0) + 1;
    });
  }

  let best = "Konaseema, East Godavari, Andhra Pradesh, India";
  let bestCount = 0;
  Object.entries(counts).forEach(([query, count]) => {
    if (count > bestCount) {
      bestCount = count;
      best = query;
    }
  });

  return best;
}

/** Human-readable day-by-day summary used for the share, copy and print views. */
export function buildItineraryText(plan: Record<number, string[]>, dayCount: number): string {
  const lookup = buildPoolLookup();
  const summary = summarizeItinerary(plan, dayCount);

  const lines: string[] = [];
  lines.push(
    `My Rythugaru Konaseema itinerary — ${summary.days} ${summary.days === 1 ? "day" : "days"}, ${summary.stops} ${
      summary.stops === 1 ? "stop" : "stops"
    }`,
  );
  lines.push("");

  for (let d = 0; d < dayCount; d += 1) {
    const ids = plan[d] || [];
    if (!ids.length) continue;
    lines.push(`Day ${d + 1}`);
    ids.forEach((id) => {
      const item = lookup[id];
      if (item) lines.push(`• ${item.title} (${item.duration})`);
    });
    lines.push("");
  }

  lines.push(`Estimated time: ${summary.hours} hours · Rough budget: ${formatRupees(summary.cost)}`);
  lines.push("");
  lines.push("Planned with Rythugaru Agri Tourism.");
  return lines.join("\n");
}